/**
 * Costing arithmetic for a corrugated box.
 *
 * Ported from the standalone "Opal Print 'N' Pack – Costing Sheet"
 * (`inputs/Opal-Costing-Sheet.html`), then corrected where that sheet could
 * not describe the board Opal actually makes.
 *
 * **Where this deliberately differs from the sheet it came from.** The sheet
 * had a "Paper" with one GSM and a rate, plus four "Flute GSM" fields where 2
 * and 4 were silently the fluted ones, plus a white back board bolted on
 * beside them. That could not express a 7-ply board at all, let the top layer
 * be costed twice, and allowed boards that do not exist -- four flutes with a
 * white back stacked on top.
 *
 * A board here is what it is on the floor: a mono carton, or 3, 5 or 7 plies, alternating.
 *
 *   ply 1        the printed face, kraft or white back
 *   even plies   flute, always kraft, and they take up extra paper
 *   odd plies    liner, kraft or white back
 *
 * Every layer is costed the same way and the board is their sum, so there is
 * no bolt-on and nothing counted twice. Because of that, the pre-ply figures
 * the old sheet produced no longer match: it was adding a printed sheet to a
 * separate stack. These numbers are right by construction, and
 * `app/tests/costing.test.ts` works them out from the rules rather than
 * capturing them from the original.
 *
 * Everything here is pre-GST, like the rest of the system.
 *
 * Deliberately pure and DOM-free. This is the only copy of the maths: the
 * server stores the inputs a costing was entered with and nothing derived
 * from them.
 */

/** Breakdown rows, in the order they print. */
export const COST_LINES = [
  'Board',
  'Plate & printing',
  'Additional',
  'Lamination',
  'Dripoff',
  'Flutes & liners',
  'Die cutting',
  'Flop pasting',
  'Finish',
] as const;

/** sq in per m² (1550) × g per kg (1000). sq in × GSM ÷ this = kg. */
const SQIN_GSM_TO_KG = 1_550_000;

/** Demy up to these sides, Double Demy above. Each costing carries its own
 *  limits from Settings; these stand in only for a costing saved before it
 *  did. `DD` in the column names is Double Demy; the screen says it in full. */
const STANDARD_DEMY_SHORT_IN = 22;
const STANDARD_DEMY_LONG_IN = 32;

/** 1 is a mono carton; a corrugated board starts and ends with a liner, so the count is odd. */
export const PLY_OPTIONS = [1, 3, 5, 7] as const;
export type Ply = (typeof PLY_OPTIONS)[number];

export const plyLabel = (ply: number | string) => (Number(ply) === 1 ? 'Mono carton' : `${Number(ply)} ply`);

/**
 * What a layer is made of.
 *
 * Normal board and kraft are different papers bought at different rates, not
 * two names for one thing -- the printed face can be any of the three, while
 * a liner is kraft or white back and a flute is always kraft.
 */
export type PlyMaterial = 'NORMAL_BOARD' | 'KRAFT' | 'WHITE_BACK';

export type PlyRole = 'PRINTED' | 'FLUTE' | 'LINER';

export const MATERIAL_LABEL: Record<PlyMaterial, string> = {
  NORMAL_BOARD: 'Normal board',
  KRAFT: 'Kraft',
  WHITE_BACK: 'White back',
};

/** What a layer in this position may be made of. */
export function materialsFor(role: PlyRole): PlyMaterial[] {
  if (role === 'FLUTE') return ['KRAFT'];
  // The printed face can be any of the three; a liner is kraft or white back.
  return role === 'PRINTED'
    ? ['NORMAL_BOARD', 'KRAFT', 'WHITE_BACK']
    : ['KRAFT', 'WHITE_BACK'];
}

export interface CostingInputs {
  /** Sheet label "Board H", inches. */
  boardHeightIn: number;
  /** Sheet label "Board L", inches. */
  boardLengthIn: number;
  /** Boxes wanted. The sheets to run are derived from this and ups. */
  boxQty: number;
  /** Boxes per sheet. */
  ups: number;
  /**
   * Sheets allowed for make-ready and spoilage, added to the run. Opal's
   * figure is 50. Zero is a real answer, so nothing requires it.
   */
  wastageSheets: number;

  /** ₹ per kg for normal printing board. */
  paperRate: number;
  /** ₹ per kg for kraft, wherever it is used. */
  ccpRate: number;
  /** ₹ per kg for any layer made of white back. */
  whiteBackRate: number;

  /** 1 (mono carton), 3, 5 or 7. */
  ply: number;
  plyGsm1: number;
  plyGsm2: number;
  plyGsm3: number;
  plyGsm4: number;
  plyGsm5: number;
  plyGsm6: number;
  plyGsm7: number;
  /** Only the liners have a choice -- the even plies are always kraft, which
   *  is why there is no material for 2, 4 or 6. */
  plyMaterial1: PlyMaterial;
  plyMaterial3: PlyMaterial;
  plyMaterial5: PlyMaterial;
  plyMaterial7: PlyMaterial;

  /** ₹ per 100 sq in of sheet. */
  laminationRate: number;
  /** ₹ per 100 sq in of sheet. */
  dripoffRate: number;

  /** ₹ per 1000 sheets. */
  dieCuttingRate: number;
  /** ₹ per box. */
  flopPastingRate: number;
  /** Multiplier on whichever "additional" rate the size selects. */
  additionalUnits: number;

  /** Plate & printing, ₹, Demy. */
  demyPlate: number;
  /** Plate & printing, ₹, Double Demy. */
  ddPlate: number;
  /** Additional, ₹ per unit, Demy. */
  demyAdditionalRate: number;
  /** Additional, ₹ per unit, Double Demy. */
  ddAdditionalRate: number;
  /** ₹, charged the same on both sizes. */
  finishCharge: number;

  /** Extra paper a fluted layer takes up, as a % of its GSM. */
  fluteExtraPct: number;

  /** The biggest sheet still costed as Demy, short side and long side, inches. */
  demyMaxShortIn: number;
  demyMaxLongIn: number;
}

export const COSTING_DEFAULTS: CostingInputs = {
  boardHeightIn: 0, boardLengthIn: 0, boxQty: 0, ups: 0, wastageSheets: 50,
  paperRate: 0, ccpRate: 0, whiteBackRate: 0,

  ply: 3,
  plyGsm1: 0, plyGsm2: 0, plyGsm3: 0, plyGsm4: 0, plyGsm5: 0, plyGsm6: 0, plyGsm7: 0,
  // The face defaults to ordinary printing board; the liners to kraft.
  plyMaterial1: 'NORMAL_BOARD',
  plyMaterial3: 'KRAFT', plyMaterial5: 'KRAFT', plyMaterial7: 'KRAFT',

  laminationRate: 0, dripoffRate: 0,
  dieCuttingRate: 500, flopPastingRate: 0.4, additionalUnits: 0,
  demyPlate: 0, ddPlate: 0, demyAdditionalRate: 600, ddAdditionalRate: 1000,
  finishCharge: 200, fluteExtraPct: 40,
  demyMaxShortIn: STANDARD_DEMY_SHORT_IN, demyMaxLongIn: STANDARD_DEMY_LONG_IN,
};

/** The two kinds of field, split by what the defaults hold -- so adding one
 *  to the interface puts it in the right list automatically. */
export const COSTING_NUMBER_FIELDS = (Object.keys(COSTING_DEFAULTS) as (keyof CostingInputs)[])
  .filter((k) => typeof COSTING_DEFAULTS[k] === 'number');
export const COSTING_MATERIAL_FIELDS = (Object.keys(COSTING_DEFAULTS) as (keyof CostingInputs)[])
  .filter((k) => typeof COSTING_DEFAULTS[k] === 'string');
export const COSTING_FIELDS = [...COSTING_NUMBER_FIELDS, ...COSTING_MATERIAL_FIELDS];

/**
 * A field name as the database spells it: plyGsm1 -> ply_gsm_1.
 *
 * Here rather than on a screen because two screens now read rows back into
 * inputs -- the sheet and the list -- and two copies of this mapping is how
 * one of them ends up quietly reading the wrong column.
 */
export const camelToSnake = (s: string) =>
  s.replace(/([a-z])([0-9A-Z])/g, '$1_$2').toLowerCase();

export interface PlyLayer {
  position: number;
  role: PlyRole;
  material: PlyMaterial;
  gsm: number;
  /** GSM actually consumed: a flute takes up more paper than it measures. */
  effectiveGsm: number;
  /** ₹ per kg this layer is bought at. */
  rate: number;
  cost: number;
}

export interface SizeCosting {
  /** One amount per COST_LINES entry, same order. */
  lines: number[];
  total: number;
  /** null until a quantity and ups are entered. */
  perBox: number | null;
}

export type SheetSize = 'DEMY' | 'DD';

export interface CostingResult {
  /** Boxes wanted -- what was typed. */
  boxes: number;
  /** Sheets needed, rounded up: a sheet cannot be run in part. */
  sheets: number;
  /** Every layer of the board, ply 1 first. */
  layers: PlyLayer[];
  /** The printed face alone -- the "Board" line. */
  printedFace: number;
  /** Plies 2 and down -- the "Flutes & liners" line. */
  boardBelowFace: number;
  /** All layers, with the flute take-up counted. */
  totalGsm: number;
  sheetAreaM2: number;
  /** Board weight of one sheet, in grams, every layer included. */
  sheetWeightG: number;
  totalWeightKg: number;
  demy: SizeCosting;
  dd: SizeCosting;
  size: SheetSize | null;
}

/** Blank or unparseable is zero, the way the sheet always treated it. */
const n = (v: number): number => (Number.isFinite(v) ? v : 0);

/** Sides are compared long-to-long, so a sheet typed the other way round
 *  still costs as the same size. */
export function sheetSizeFor(
  heightIn: number,
  lengthIn: number,
  demyMaxShortIn = STANDARD_DEMY_SHORT_IN,
  demyMaxLongIn = STANDARD_DEMY_LONG_IN,
): SheetSize | null {
  const long = Math.max(n(heightIn), n(lengthIn));
  const short = Math.min(n(heightIn), n(lengthIn));
  if (!(long > 0 && short > 0)) return null;
  const limit = demyLimitsOf({ demyMaxShortIn, demyMaxLongIn });
  return short > limit.short || long > limit.long ? 'DD' : 'DEMY';
}

export function demyLimitsOf(input: Pick<CostingInputs, 'demyMaxShortIn' | 'demyMaxLongIn'>) {
  return {
    short: n(input.demyMaxShortIn) > 0 ? n(input.demyMaxShortIn) : STANDARD_DEMY_SHORT_IN,
    long: n(input.demyMaxLongIn) > 0 ? n(input.demyMaxLongIn) : STANDARD_DEMY_LONG_IN,
  };
}

const GSM_AT: Record<number, keyof CostingInputs> = {
  1: 'plyGsm1', 2: 'plyGsm2', 3: 'plyGsm3', 4: 'plyGsm4',
  5: 'plyGsm5', 6: 'plyGsm6', 7: 'plyGsm7',
};
const MATERIAL_AT: Record<number, keyof CostingInputs> = {
  1: 'plyMaterial1', 3: 'plyMaterial3', 5: 'plyMaterial5', 7: 'plyMaterial7',
};

/** Ply 1 prints, even plies flute, the rest line. */
export function roleOf(position: number): PlyRole {
  if (position === 1) return 'PRINTED';
  return position % 2 === 0 ? 'FLUTE' : 'LINER';
}

/** The layers a board of this ply count has, in order. */
export function layersOf(input: CostingInputs): PlyLayer[] {
  const sqin = n(input.boardHeightIn) * n(input.boardLengthIn);
  const sheets = sheetsFor(input);
  const ply = n(input.ply);

  const layers: PlyLayer[] = [];
  for (let position = 1; position <= ply; position++) {
    const role = roleOf(position);
    // A flute is always kraft; only a liner or the printed face has a choice.
    const material: PlyMaterial = role === 'FLUTE'
      ? 'KRAFT'
      : (input[MATERIAL_AT[position]!] as PlyMaterial) ?? 'KRAFT';

    const gsm = n(input[GSM_AT[position]!] as number);
    // Fluting consumes more paper than it measures flat, because it is
    // corrugated before it is stuck down.
    const effectiveGsm = role === 'FLUTE' ? gsm * (1 + n(input.fluteExtraPct) / 100) : gsm;

    // The rate follows the paper, wherever it sits. Normal board on the face
    // and kraft underneath are priced as what they are, not as where they are.
    const rate = material === 'WHITE_BACK' ? n(input.whiteBackRate)
      : material === 'NORMAL_BOARD' ? n(input.paperRate)
      : n(input.ccpRate);

    layers.push({
      position, role, material, gsm, effectiveGsm, rate,
      cost: ((sqin * effectiveGsm) / SQIN_GSM_TO_KG) * rate * sheets,
    });
  }
  return layers;
}

/**
 * Sheets actually run, which is more than the boxes need.
 *
 * Sheets are bought and fed in whole, so the count rounds up -- half a sheet
 * cannot be run. On top of that a press eats sheets being set up and spoils
 * more while it runs, and those sheets are bought exactly like the good ones.
 * Charging the paper, board, lamination and die cutting on ceil(boxes / ups)
 * alone bills the job for less paper than it consumes.
 *
 * The allowance rides on a run, not on an intention: with no ups there is no
 * run, so nothing is wasted and nothing is allowed.
 */
export function sheetsFor(input: CostingInputs): number {
  const ups = n(input.ups);
  if (!(ups > 0)) return 0;
  return Math.ceil(n(input.boxQty) / ups) + n(input.wastageSheets);
}

export function calculateCosting(input: CostingInputs): CostingResult {
  const sqin = n(input.boardHeightIn) * n(input.boardLengthIn);
  const boxes = n(input.boxQty);
  const sheets = sheetsFor(input);

  const layers = layersOf(input);
  const printedFace = layers.find((l) => l.position === 1)?.cost ?? 0;
  const boardBelowFace = layers.filter((l) => l.position > 1)
    .reduce((sum, l) => sum + l.cost, 0);
  const totalGsm = layers.reduce((sum, l) => sum + l.effectiveGsm, 0);

  const lamination = (sqin / 100) * n(input.laminationRate) * sheets;
  const dripoff = (sqin / 100) * n(input.dripoffRate) * sheets;
  const dieCutting = (n(input.dieCuttingRate) * sheets) / 1000;
  const flopPasting = n(input.flopPastingRate) * boxes;
  const finish = n(input.finishCharge);

  const forSize = (plate: number, additionalRate: number): SizeCosting => {
    const lines = [
      printedFace,
      n(plate),
      n(input.additionalUnits) * n(additionalRate),
      lamination,
      dripoff,
      boardBelowFace,
      dieCutting,
      flopPasting,
      finish,
    ];
    const total = lines.reduce((a, b) => a + b, 0);
    return { lines, total, perBox: boxes ? total / boxes : null };
  };

  const kgPerSheet = (sqin * totalGsm) / SQIN_GSM_TO_KG;

  return {
    boxes,
    sheets,
    layers,
    printedFace,
    boardBelowFace,
    totalGsm,
    sheetAreaM2: sqin / 1550,
    sheetWeightG: kgPerSheet * 1000,
    totalWeightKg: kgPerSheet * sheets,
    demy: forSize(input.demyPlate, input.demyAdditionalRate),
    dd: forSize(input.ddPlate, input.ddAdditionalRate),
    size: sheetSizeFor(input.boardHeightIn, input.boardLengthIn, input.demyMaxShortIn, input.demyMaxLongIn),
  };
}

/* -------------------------------------------------------------------------
 * Is this costing complete enough to price?
 * ---------------------------------------------------------------------- */

/**
 * What a costing still needs before a cost may be shown.
 *
 * Every figure on this screen is a product. Leave any one factor out and the
 * arithmetic does not fail -- it quietly returns a smaller number, and a
 * smaller number looks exactly like a right one. Measured on one real box
 * (22 x 23 in, 3,100 boxes, 2 ups, 3 ply 230/120/120):
 *
 *     complete and correct          Rs 25,855    Rs 8.34 / box
 *     ups left at 0                 Rs  5,550    Rs 1.79 / box   79% under
 *     paper and kraft rates at 0    Rs 11,733    Rs 3.78 / box   55% under
 *     ply GSMs left blank           Rs 11,733    Rs 3.78 / box   55% under
 *
 * All three used to render with the confidence of the first. The guard this
 * replaces asked whether the *board* had been started, so it caught a
 * part-filled board and nothing else; an empty board, a missing ups and an
 * unpriced paper all walked straight through.
 *
 * Only the factors the board cannot be priced without are required. A charge
 * that is legitimately nil stays optional -- no lamination is a real choice
 * (the screen offers it as a chip), and a repeat job reusing its plates
 * really does cost nothing to plate. Requiring those would cry wolf, and a
 * guard people learn to dismiss protects nobody.
 */
export interface CostingReadiness {
  /**
   * Nothing has been entered yet. A new sheet is new, not wrong, so the
   * screen greets it rather than listing everything it lacks.
   */
  untouched: boolean;
  /** What is needed and absent, named as the screen labels it. */
  missing: string[];
}

export function readinessOf(input: CostingInputs): CostingReadiness {
  /**
   * What describes this box. Only a person can enter these, so they are what
   * "has anybody started?" actually means.
   *
   * The rates below are deliberately not part of that question. They arrive
   * filled in from the rate list, so counting them would make every brand new
   * sheet look started and greet it with a list of everything it lacks --
   * which is exactly what filling them in was meant to avoid.
   */
  const box: [string, number][] = [
    ['Box quantity', n(input.boxQty)],
    ['Ups', n(input.ups)],
    ['Board H', n(input.boardHeightIn)],
    ['Board L', n(input.boardLengthIn)],
  ];
  const needed: [string, number][] = [...box];

  // Every layer the chosen ply count actually has. layersOf is the one place
  // that knows which material sits where, so asking it keeps this rule and
  // the arithmetic from ever disagreeing about the board.
  const layers = layersOf(input);
  for (const l of layers) {
    box.push([`Ply ${l.position} GSM`, l.gsm]);
    needed.push([`Ply ${l.position} GSM`, l.gsm]);
  }

  // ...and the rate for each paper actually used, once each. A white back
  // rate is not missing from a board with no white back in it.
  const RATE_OF: Record<PlyMaterial, [string, number]> = {
    NORMAL_BOARD: ['Normal board rate', n(input.paperRate)],
    KRAFT: ['CCP / kraft rate', n(input.ccpRate)],
    WHITE_BACK: ['White back rate', n(input.whiteBackRate)],
  };
  for (const material of new Set(layers.map((l) => l.material))) {
    needed.push(RATE_OF[material]);
  }

  return {
    untouched: box.every(([, v]) => !(v > 0)),
    missing: needed.filter(([, v]) => !(v > 0)).map(([label]) => label),
  };
}

/**
 * What a saved costing came to a box, or null when it cannot be priced.
 *
 * Anywhere a stored costing is shown as a figure -- a list, a deleted row --
 * has the same duty the sheet has: a costing missing a factor multiplies out
 * to a smaller, plausible number rather than an obvious error. The sheet
 * learned to withhold it; a list that quietly prices the same costing anyway
 * puts the wrong figure back in front of someone, in the one place they are
 * scanning quickly rather than reading carefully.
 */
export function perBoxOf(input: CostingInputs): number | null {
  if (readinessOf(input).missing.length > 0) return null;
  const r = calculateCosting(input);
  return (r.size === 'DD' ? r.dd : r.demy).perBox;
}

/** The whole job, or null on the same terms as `perBoxOf`. */
export function totalOf(input: CostingInputs, size: 'demy' | 'dd'): number | null {
  if (readinessOf(input).missing.length > 0) return null;
  return calculateCosting(input)[size].total;
}
